import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { useInstaladores } from "@/hooks/useInstaladores";
import { Search, Wrench } from "lucide-react";

interface InstaladorSearchSelectProps {
  value: string;
  onChange: (instaladorId: string, instaladorNome: string, instaladorContato: string, instaladorCpfCnpj: string) => void;
  label?: string;
}

export function InstaladorSearchSelect({ 
  value, 
  onChange, 
  label = "Selecionar Instalador" 
}: InstaladorSearchSelectProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [selectedInstaladorName, setSelectedInstaladorName] = useState("");
  
  const { data: instaladores, isLoading } = useInstaladores();

  // Atualizar o nome do instalador selecionado quando o value muda
  useEffect(() => {
    if (value && instaladores) {
      const instalador = instaladores.find(i => i.id.toString() === value);
      if (instalador) {
        setSelectedInstaladorName(instalador.nome);
        setSearchTerm(instalador.nome);
      }
    } else {
      setSelectedInstaladorName("");
      setSearchTerm("");
    }
  }, [value, instaladores]);

  const filteredInstaladores = instaladores?.filter(instalador => {
    const searchLower = searchTerm.toLowerCase();
    return (
      instalador.nome.toLowerCase().includes(searchLower) ||
      instalador.cpf_cnpj?.toLowerCase().includes(searchLower) ||
      instalador.contato?.toLowerCase().includes(searchLower) ||
      instalador.id.toString().includes(searchLower)
    );
  }) || [];

  const handleInstaladorSelect = (instalador: any) => {
    onChange(
      instalador.id.toString(), 
      instalador.nome, 
      instalador.contato || "", 
      instalador.cpf_cnpj || ""
    );
    setSelectedInstaladorName(instalador.nome);
    setSearchTerm(instalador.nome);
    setIsOpen(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    setIsOpen(newValue.length > 0);
    
    // Se o input estiver vazio, limpar a seleção
    if (newValue === "") {
      onChange("", "", "", "");
      setSelectedInstaladorName("");
    }
  };

  const handleInputFocus = () => {
    setIsOpen(true);
    if (searchTerm === "" && selectedInstaladorName !== "") {
      setSearchTerm("");
    }
  };

  const formatCpfCnpj = (cpfCnpj: string) => {
    if (!cpfCnpj) return "";
    if (cpfCnpj.length <= 11) {
      return cpfCnpj.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
    } else {
      return cpfCnpj.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
    }
  };

  return (
    <div className="relative">
      <Label htmlFor="instalador-search">{label}</Label>
      <div className="relative mt-1">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          id="instalador-search"
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          placeholder={isLoading ? "Carregando instaladores..." : "Digite ID, nome, CPF/CNPJ ou contato..."}
          className="pl-10 w-full"
          disabled={isLoading}
        />
      </div>

      {isOpen && filteredInstaladores.length > 0 && (
        <Card className="absolute z-50 w-full mt-1 max-h-60 overflow-y-auto border shadow-lg bg-white">
          <div className="p-2">
            {filteredInstaladores.map((instalador) => (
              <div
                key={instalador.id}
                onClick={() => handleInstaladorSelect(instalador)}
                className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer rounded-md border-b last:border-b-0"
              >
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <Wrench className="h-4 w-4 text-blue-600" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-blue-600 bg-blue-50 px-2 py-1 rounded border">
                      {instalador.id}
                    </span>
                    <span className="font-medium text-gray-900">{instalador.nome}</span>
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    {instalador.cpf_cnpj && (
                      <span className="bg-green-50 text-green-700 px-2 py-1 rounded text-xs">
                        {formatCpfCnpj(instalador.cpf_cnpj)}
                      </span>
                    )}
                    {instalador.contato && (
                      <span className="ml-2 text-gray-500">
                        {instalador.contato}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {isOpen && searchTerm && filteredInstaladores.length === 0 && !isLoading && (
        <Card className="absolute z-50 w-full mt-1 border shadow-lg bg-white">
          <div className="p-4 text-center text-gray-500">
            Nenhum instalador encontrado
          </div>
        </Card>
      )}
    </div>
  );
} 